import { normalizeRational, type Equation } from '../../shared/algebra.ts';
import type { Focus } from './lessonScene.ts';
import type { NarratedWord } from './narratedOperation.ts';

export type MathTarget = { id: string; side: 'left'|'right'|'relation'; role: 'variable'|'coefficient'|'constant'|'equals'; value: string };
export type ReplyAlignment = { focus: Focus; pointerFocus: Focus; target?: MathTarget; word: string; normalizedToken: string; wordIndex: number; excerpt: string; excerptStartWord: number; excerptEndWord: number; textRange?: {start:number;end:number} };
const numberWords:Record<string,number>={zero:0,one:1,two:2,three:3,four:4,five:5,six:6,seven:7,eight:8,nine:9,ten:10,eleven:11,twelve:12,thirteen:13,fourteen:14,fifteen:15,sixteen:16,seventeen:17,eighteen:18,nineteen:19,twenty:20,thirty:30,forty:40,fifty:50,sixty:60,seventy:70,eighty:80,ninety:90};
const token=(text:string)=>text.toLowerCase().trim().replace(/[−–]/g,'-').replace(/^[$“"'(]+/,'').replace(/[.,!?;:”"')]+$/,'');
const endsSentence=(text:string)=>/[.!?]["'”’]?$/.test(text);
const numeric=(text:string)=>numberWords[text]===undefined?text:String(numberWords[text]);

function targetsFor(equation:Equation):MathTarget[]{
  return (['left','right'] as const).flatMap(side=>{
    const terms=equation[side],result:MathTarget[]=[];
    if(terms.x!=='0')result.push({id:`${side}.x`,side,role:'variable',value:'x'},{id:`${side}.coefficient`,side,role:'coefficient',value:normalizeRational(terms.x)});
    if(terms.constant!=='0'||terms.x==='0')result.push({id:`${side}.constant`,side,role:'constant',value:normalizeRational(terms.constant)});
    return result;
  });
}
function resolveWord(words:readonly NarratedWord[],index:number,targets:MathTarget[]) {
  const current=token(words[index].text);
  let start=Math.max(0,index-5);
  for(let i=start;i<index;i++)if(endsSentence(words[i].text)||/^(equals|=)$/.test(token(words[i].text)))start=i+1;
  const context=words.slice(start,index).map(w=>token(w.text));
  const side=context.includes('left')?'left':context.includes('right')?'right':undefined;
  const roleContext=context.findLast(w=>/^(coefficient|constant|rate|total|receipt|hours?|unknown)$/.test(w));
  let candidates=targets,alias:Focus|undefined;
  if(side)candidates=candidates.filter(t=>t.side===side);
  if(/^(=|equals?|equality|balance)$/.test(current)||(current==='sides'&&context.at(-1)==='both'))return {focus:'equals' as Focus,target:{id:'equals',side:'relation',role:'equals',value:'='} as MathTarget,normalizedToken:current};
  if(/^(x|unknown|variable|hours?)$/.test(current)||/^-?\d+(?:[./]\d+)?x$/.test(current)){
    candidates=candidates.filter(t=>t.role==='variable');
    alias=/^hours?$/.test(current)?'hours':'x';
  }else if(/^(coefficient|rate|constant|total|receipt)$/.test(current)){
    candidates=candidates.filter(t=>current==='coefficient'||current==='rate'?t.role==='coefficient':current==='total'||current==='receipt'?t.role==='constant'&&t.side==='right':t.role==='constant');
    alias=current==='rate'?'rate':undefined;
  }else{
    let value=numeric(current);
    const previous=token(words[index-1]?.text??'');
    if(token(words[index+1]?.text??'')==='over')return {focus:'none' as Focus,normalizedToken:value};
    if(previous==='over'&&index>=2){value=`${numeric(token(words[index-2].text))}/${value}`;if(token(words[index-3]?.text??'')==='negative')value=`-${value}`;}
    else if(previous==='negative')value=`-${value}`;
    else if((numberWords[previous]??0)>=20&&(numberWords[previous]??0)%10===0&&(numberWords[current]??0)>0&&(numberWords[current]??10)<10)value=String(numberWords[previous]+numberWords[current]);
    try{value=normalizeRational(value);}catch{return {focus:'none' as Focus,normalizedToken:current};}
    candidates=candidates.filter(t=>t.role!=='variable'&&t.value===value);
    if(candidates.length>1&&roleContext)candidates=candidates.filter(t=>roleContext==='rate'||roleContext==='coefficient'?t.role==='coefficient':roleContext==='total'||roleContext==='receipt'?t.role==='constant'&&t.side==='right':roleContext==='constant'?t.role==='constant':false);
    if(roleContext==='rate'&&candidates[0]?.role==='coefficient')alias='rate';
    return resolved(candidates,value,alias);
  }
  return resolved(candidates,current,alias);
}
function resolved(candidates:MathTarget[],normalizedToken:string,alias?:Focus){
  if(candidates.length!==1)return {focus:'none' as Focus,normalizedToken};
  const target=candidates[0];
  const focus:Focus=alias??(target.role==='variable'?'x':target.role==='coefficient'?'coefficient':target.side==='left'?'constant':'total');
  return {focus,target,normalizedToken};
}

/** No estimates or broad context guesses: resolve explicit words against this page's equation. */
export function replyAlignment({text,equation,words=[],positionMs}:{text:string;equation:Equation;words?:readonly NarratedWord[];positionMs:number}):ReplyAlignment{
  const neutral:ReplyAlignment={focus:'none',pointerFocus:'none',word:'',normalizedToken:'',wordIndex:-1,excerpt:'',excerptStartWord:-1,excerptEndWord:-1};
  if(!Number.isFinite(positionMs)||positionMs<0||words.some((w,i)=>!Number.isFinite(w.startMs)||!Number.isFinite(w.endMs)||w.startMs<0||w.endMs<=w.startMs||(i>0&&w.startMs<words[i-1].startMs)))return neutral;
  const index=words.findIndex(w=>positionMs>=w.startMs&&positionMs<w.endMs);
  let targets:MathTarget[];try{targets=targetsFor(equation);}catch{return neutral;}
  if(index<0)return neutral;
  const cue=resolveWord(words,index,targets);
  let start=index,end=index;
  while(start>0&&!endsSentence(words[start-1].text))start--;
  while(end<words.length-1&&!endsSentence(words[end].text))end++;
  return {...cue,pointerFocus:cue.focus,word:words[index].text,wordIndex:index,excerpt:words.slice(start,end+1).map(w=>w.text).join(' '),excerptStartWord:start,excerptEndWord:end,textRange:alignedTextRange(text,words,index)};
}

/** Exact source spans only. Unmatched ASR tokens never invent or replace note text. */
function alignedTextRange(text:string,words:readonly NarratedWord[],index:number){
  const pieces=[...text.matchAll(/[A-Za-z]+|\d+(?:[./]\d+)?|[=+−×÷-]/g)];
  let cursor=0,result:{start:number;end:number}|undefined;
  for(let i=0;i<=index;i++){
    const wanted=numeric(token(words[i].text));
    const found=pieces.findIndex((p,j)=>j>=cursor&&j<cursor+8&&numeric(token(p[0]))===wanted);
    result=found<0?undefined:{start:pieces[found].index!,end:pieces[found].index!+pieces[found][0].length};
    if(found>=0)cursor=found+1;
  }
  return result;
}
